import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { LockKey, EnvelopeSimple, ShieldCheck, ArrowLeft } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminLogin() {
  const [step, setStep] = useState('credentials'); // 'credentials' | 'otp'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const otpRefs = useRef([]);
  const { checkAuth } = useAuth();
  const navigate = useNavigate();

  const formatErr = (detail) => {
    if (!detail) return 'Something went wrong. Try again.';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
    return String(detail);
  };

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setError(''); setInfo(''); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      if (data.status === 'otp_sent') {
        setInfo(data.message);
        setStep('otp');
        setTimeout(() => otpRefs.current[0]?.focus(), 100);
      }
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').trim().slice(0, 6);
    if (/^\d{1,6}$/.test(pasted)) {
      const newOtp = pasted.split('').concat(Array(6 - pasted.length).fill(''));
      setOtp(newOtp);
      otpRefs.current[Math.min(pasted.length, 5)]?.focus();
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    const code = otp.join('');
    if (code.length !== 6) {
      setError('Enter the full 6-digit code');
      setLoading(false);
      return;
    }
    try {
      await axios.post(`${API}/auth/verify-otp`, { email, otp: code }, { withCredentials: true });
      await checkAuth();
      navigate('/admin/dashboard');
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const backToCredentials = () => {
    setStep('credentials');
    setOtp(['', '', '', '', '', '']);
    setError(''); setInfo('');
  };

  return (
    <div className="min-h-screen bg-[#050810] flex items-center justify-center px-6 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-cyan-400 rounded-full"
            style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%` }}
            animate={{ y: [0, -30, 0], opacity: [0.2, 0.8, 0.2] }}
            transition={{ duration: 3 + Math.random() * 2, repeat: Infinity, delay: Math.random() * 2 }}
          />
        ))}
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-md relative z-10">
        <div className="card-glass rounded-xl p-8 sm:p-10">
          <AnimatePresence mode="wait">
            {step === 'credentials' ? (
              <motion.div key="creds" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>
                <div className="text-center mb-8">
                  <div className="w-20 h-20 mx-auto mb-4 bg-cyan-500/10 rounded-full flex items-center justify-center border-2 border-cyan-500/30">
                    <LockKey size={40} weight="duotone" className="text-cyan-400" />
                  </div>
                  <h1 className="text-3xl font-black font-['Unbounded'] text-white hero-glow mb-2">Admin Portal</h1>
                  <p className="text-slate-400 text-sm">Step 1 of 2 — Verify your credentials</p>
                </div>

                <form onSubmit={handleCredentialsSubmit} className="space-y-6">
                  {error && (
                    <div data-testid="login-error" className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400 text-sm">{error}</div>
                  )}
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Email</label>
                    <div className="relative">
                      <input
                        data-testid="email-input"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="w-full bg-[#0F1528] border border-cyan-500/20 rounded-lg px-12 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
                        placeholder="you@example.com"
                      />
                      <EnvelopeSimple className="absolute left-4 top-1/2 -translate-y-1/2 text-cyan-400" size={20} weight="duotone" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Password</label>
                    <div className="relative">
                      <input
                        data-testid="password-input"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="w-full bg-[#0F1528] border border-cyan-500/20 rounded-lg px-12 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
                        placeholder="••••••••"
                      />
                      <LockKey className="absolute left-4 top-1/2 -translate-y-1/2 text-cyan-400" size={20} weight="duotone" />
                    </div>
                  </div>
                  <motion.button
                    data-testid="login-button"
                    type="submit"
                    disabled={loading}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full bg-cyan-500 text-black font-bold px-6 py-4 rounded-md uppercase tracking-wide shadow-[0_0_20px_rgba(0,240,255,0.4)] hover:bg-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Verifying...' : 'Continue'}
                  </motion.button>
                </form>

                <div className="mt-6 flex justify-between text-sm">
                  <Link to="/admin/forgot-password" className="text-cyan-400 hover:text-cyan-300">Forgot password?</Link>
                  <Link to="/" className="text-slate-400 hover:text-slate-300">← Home</Link>
                </div>
              </motion.div>
            ) : (
              <motion.div key="otp" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <div className="text-center mb-8">
                  <div className="w-20 h-20 mx-auto mb-4 bg-cyan-500/10 rounded-full flex items-center justify-center border-2 border-cyan-500/30">
                    <ShieldCheck size={40} weight="duotone" className="text-cyan-400" />
                  </div>
                  <h1 className="text-2xl font-black font-['Unbounded'] text-white hero-glow mb-2">Verification</h1>
                  <p className="text-slate-400 text-sm">Step 2 of 2 — Enter the 6-digit code sent to <span className="text-cyan-400">{email}</span></p>
                </div>

                <form onSubmit={handleOtpSubmit} className="space-y-6">
                  {info && (
                    <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3 text-cyan-300 text-xs">{info}</div>
                  )}
                  {error && (
                    <div data-testid="otp-error" className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">{error}</div>
                  )}
                  <div className="flex justify-between gap-2" onPaste={handleOtpPaste}>
                    {otp.map((digit, i) => (
                      <input
                        key={i}
                        data-testid={`otp-input-${i}`}
                        ref={(el) => (otpRefs.current[i] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(i, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(i, e)}
                        className="w-12 h-14 text-center text-2xl font-bold bg-[#0F1528] border border-cyan-500/20 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/30"
                      />
                    ))}
                  </div>
                  <motion.button
                    data-testid="verify-otp-button"
                    type="submit"
                    disabled={loading}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full bg-cyan-500 text-black font-bold px-6 py-4 rounded-md uppercase tracking-wide shadow-[0_0_20px_rgba(0,240,255,0.4)] hover:bg-cyan-400 disabled:opacity-50"
                  >
                    {loading ? 'Verifying...' : 'Verify & Sign In'}
                  </motion.button>
                </form>

                <button onClick={backToCredentials} className="mt-6 flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-400 mx-auto">
                  <ArrowLeft size={16} /> Back to login
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
